import { GetSurveysQuery, SurveyFragment } from '../../graphql/types.generated';
import { IQuestion, SanitizedSurvey, SurveyFormValues } from './survey.types';
import { isSurveyValid } from './survey.validation';
import { buildGetIsDismissed } from '../../components/Survey/get-dismissed-surveys.utils';
import { AwardPoints, AwardType } from '../offers/award.types';
import { AwardVouchersSurvey, SurveyAward } from '../../components/Survey/survey.types';

export const getFirstNotDismissedSurvey = async (
  membershipId: string,
  surveys: SanitizedSurvey[],
): Promise<SanitizedSurvey | null> => {
  const isDismissed = await buildGetIsDismissed(membershipId);

  const firstNotDismissedSurvey = surveys.find(({ uuid }) => !isDismissed(uuid));

  if (!firstNotDismissedSurvey) {
    return null;
  }

  return firstNotDismissedSurvey;
};

export type SanitizeSurveyParams = {
  surveys?: GetSurveysQuery['surveys'];
};

export const sanitizeSurveys = ({ surveys }: SanitizeSurveyParams): SanitizedSurvey[] => {
  if (!surveys) {
    return [];
  }
  const rawSurveys: SurveyFragment[] = surveys?.edges.map(({ node }) => node) || [];
  const validSurveys = rawSurveys.filter(isSurveyValid);

  return validSurveys.map<SanitizedSurvey>((survey) => ({ ...survey, type: 'authenticated' }));
};

export const getQuestionsFromSurvey = (survey: SanitizedSurvey): IQuestion[] => {
  const sanitizeQuestion = (question: SanitizedSurvey['questions'][0]): IQuestion => {
    return {
      uuid: question.uuid,
      min: survey.configuration.minUnits,
      max: survey.configuration.maxUnits,
      type: 'slider',
      question: question.questionText,
      measurement: survey.configuration.steeringWalletUnitOfMeasure,
      // First item because we support only one question with only one field
      name: question.fields[0].uuid,
    };
  };

  return survey.questions.map(sanitizeQuestion);
};

export const buildSurveyFormSliderInitialValues = (questions: IQuestion[]): SurveyFormValues =>
  questions.reduce(
    (accumulator, { min, max, name }) => ({
      ...accumulator,
      [name]: Math.floor((min + max) / 2),
    }),
    {},
  );

interface IGetNextQuestionUUIDParams {
  uuid: string | null;
  questions: IQuestion[];
}

interface IGetNextQuestionUUIDReturns {
  uuid: string | null;
  reason: 'no-questions' | 'no-next-question' | null;
}

export const getNextQuestionUUID = ({ uuid, questions }: IGetNextQuestionUUIDParams): IGetNextQuestionUUIDReturns => {
  if (!questions.length) {
    return { uuid: null, reason: 'no-questions' };
  }

  if (!uuid) {
    return { uuid: questions[0].uuid, reason: null };
  }

  const lastAnsweredQuestionIndex = questions.findIndex(({ uuid: currentUuid }) => currentUuid === uuid);

  if (lastAnsweredQuestionIndex === -1 || lastAnsweredQuestionIndex + 2 > questions.length) {
    return { uuid: null, reason: 'no-next-question' };
  }

  return { uuid: questions[lastAnsweredQuestionIndex + 1].uuid, reason: null };
};

export const getSurveyAward = ({ award }: SanitizedSurvey): SurveyAward => {
  if (!award) {
    return null;
  }

  if (award.__typename === 'AwardPoints') {
    const result: AwardPoints = {
      type: AwardType.Points,
      points: award.points,
    };
    return result;
  }

  if (award.__typename === 'AwardVouchers') {
    const result: AwardVouchersSurvey = {
      type: AwardType.Vouchers,
    };
    return result;
  }

  return null;
};
